import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANG } from "@/i18n";
import { useLanguages } from "@/hooks/useLanguages";

interface ConversationQuestion {
  id: string;
  question_text: string;
  question_type_id: string;
  respondent_type: "both" | "subject" | "manager";
  is_required: boolean;
  order_index: number;
  stage_id: string;
  options: any;
}

interface ConversationQuestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  question: ConversationQuestion | null;
  onSuccess: () => void;
}

export const ConversationQuestionDialog = ({
  isOpen,
  onClose,
  question,
  onSuccess,
}: ConversationQuestionDialogProps) => {
  const [formData, setFormData] = useState<{
    question_text: string;
    question_type_id: string;
    respondent_type: "both" | "subject" | "manager";
    is_required: boolean;
    order_index: number;
    stage_id: string;
    options: any;
  }>({
    question_text: "",
    question_type_id: "",
    respondent_type: "both",
    is_required: true,
    order_index: 1,
    stage_id: "",
    options: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { i18n } = useTranslation();
  const { data: languages } = useLanguages();
  const [activeLang, setActiveLang] = useState<string>(i18n.language || DEFAULT_LANG);
  const [questionTextI18n, setQuestionTextI18n] = useState<Record<string, string>>({});

  // Fetch question types for dropdown
  const { data: questionTypes } = useQuery({
    queryKey: ['question-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('question_types')
        .select('id, name')
        .order('name');

      if (error) throw error;
      return data;
    },
  });

  // Fetch stages for dropdown
  const { data: stages } = useQuery({
    queryKey: ['conversation-stages-with-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conversation_stages')
        .select(`
          id,
          name,
          conversation_templates(name)
        `)
        .order('name');

      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (question) {
      setFormData({
        question_text: question.question_text || "",
        question_type_id: question.question_type_id || "",
        respondent_type: question.respondent_type || "both",
        is_required: question.is_required || true,
        order_index: question.order_index || 1,
        stage_id: question.stage_id || "",
        options: question.options || null,
      });
      const q: any = question as any;
      setQuestionTextI18n((q.question_text_i18n as any) || {});
    } else {
      setFormData({
        question_text: "",
        question_type_id: "",
        respondent_type: "both",
        is_required: true,
        order_index: 1,
        stage_id: "",
        options: null,
      });
      setQuestionTextI18n({});
    }
  }, [question]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.question_text.trim()) {
      toast.error("Question text is required");
      return;
    }

    if (!formData.question_type_id) {
      toast.error("Question type is required");
      return;
    }

    if (!formData.stage_id) {
      toast.error("Stage selection is required");
      return;
    }

    setIsSubmitting(true);

  try {
      const submitData: any = { ...formData, question_text_i18n: questionTextI18n };
      if (question) {
        // Update existing question
        const { error } = await supabase
          .from('conversation_questions')
          .update(submitData)
          .eq('id', question.id);

        if (error) throw error;
        toast.success("Question updated successfully");
      } else {
        // Create new question
        const { error } = await supabase
          .from('conversation_questions')
          .insert([submitData]);

        if (error) throw error;
        toast.success("Question created successfully");
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error("Failed to save question");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {question ? "Edit Conversation Question" : "Create New Conversation Question"}
          </DialogTitle>
          <DialogDescription>
            {question
              ? "Update the conversation question details below."
              : "Create a new question for a conversation stage."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="question_text">Question Text</Label>
            <Textarea
              id="question_text"
              value={formData.question_text}
              onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
              placeholder="Enter the question that will be asked during the conversation..."
              rows={3}
              required
            />
          </div>

          <div className="space-y-2 border rounded-md p-3">
            <div className="flex items-center justify-between">
              <Label>Translations</Label>
              <div className="w-48">
                <Select value={activeLang} onValueChange={(v) => setActiveLang(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Language" />
                  </SelectTrigger>
                  <SelectContent>
                    {(languages || []).map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.native_name} ({l.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Localized Question ({activeLang})</Label>
              <Textarea
                value={questionTextI18n[activeLang] || ''}
                onChange={(e) => setQuestionTextI18n({ ...questionTextI18n, [activeLang]: e.target.value })}
                placeholder="Translated question text"
                rows={3}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="stage_id">Conversation Stage</Label>
            <Select
              value={formData.stage_id}
              onValueChange={(value) => setFormData({ ...formData, stage_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a stage..." />
              </SelectTrigger>
              <SelectContent>
                {stages?.map((stage) => (
                  <SelectItem key={stage.id} value={stage.id}>
                    {stage.name} ({stage.conversation_templates?.name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="question_type_id">Question Type</Label>
              <Select
                value={formData.question_type_id}
                onValueChange={(value) => setFormData({ ...formData, question_type_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select question type..." />
                </SelectTrigger>
                <SelectContent>
                  {questionTypes?.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="respondent_type">Who Answers</Label>
              <Select
                value={formData.respondent_type}
                onValueChange={(value: "both" | "subject" | "manager") => setFormData({ ...formData, respondent_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="subject">Subject Only</SelectItem>
                  <SelectItem value="manager">Manager Only</SelectItem>
                  <SelectItem value="both">Both Subject & Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="order_index">Order</Label>
              <Input
                id="order_index"
                type="number"
                min="1"
                value={formData.order_index}
                onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 1 })}
              />
            </div>

            <div className="flex items-center space-x-2 pt-6">
              <Checkbox
                id="is_required"
                checked={formData.is_required}
                onCheckedChange={(checked) => setFormData({ ...formData, is_required: checked as boolean })}
              />
              <Label htmlFor="is_required">Required question</Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : question ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};